import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import type { z } from 'zod/v4';
import { EmailSendingPreferencesSchema } from './use-load-email-sending-preferences';

const URL = '/email_sending_preferences';

const UpdateEmailSendingPreferencesVariablesSchema = EmailSendingPreferencesSchema.partial();

export type UpdateEmailSendingPreferencesVariables = z.infer<
  typeof UpdateEmailSendingPreferencesVariablesSchema
>;

export async function updateEmailSendingPreferencesFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateEmailSendingPreferencesVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateEmailSendingPreferencesVariablesSchema.parse(variables));
}

export function useUpdateEmailSendingPreferences() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateEmailSendingPreferencesVariables>({
    mutationFn: variables => updateEmailSendingPreferencesFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['emailSendingPreferences'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateEmailSendingPreferencesVariables,
    'updateEmailSendingPreferences'
  >('updateEmailSendingPreferences', mutation);
}
